<template>
	<ScopedColorTheme
		:id="`block-${$vnode.key}`"
		class="c-block-list-newsletter"
		theme="red"
	>
		<!-- Graphic -->
		<svg
			class="
				o-svg-themify
				absolute
				left-0
				bottom-0
				w-full
				max-h-full
				overflow-hidden
			"
			viewBox="0 0 864 725"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			preserveAspectRatio="xMinYMin slice"
		>
			<mask
				id="mask0_4577_142312"
				style="mask-type: alpha"
				maskUnits="userSpaceOnUse"
				x="0"
				y="0"
				width="864"
				height="725"
			>
				<rect width="864" height="725" fill="#D9D9D9" />
			</mask>
			<g mask="url(#mask0_4577_142312)">
				<g opacity="0.3" clip-path="url(#clip0_4577_142312)">
					<path
						d="M867.506 403.774L553.328 585.189L867.506 766.558V403.774Z"
						fill="#F2D2D4"
					/>
					<path
						d="M553.328 222.362V585.192L867.506 403.777L553.328 222.362Z"
						fill="#E7A6AB"
					/>
					<path
						d="M553.328 585.146L239.195 766.56L553.328 947.975L867.507 766.56L553.328 585.146Z"
						fill="#D93844"
					/>
					<path
						d="M1194.71 -148L553.328 222.362L867.506 403.776L1181.68 222.362L1810 -140.468V-148H1194.71Z"
						fill="#D93844"
					/>
				</g>
			</g>
			<defs>
				<clipPath id="clip0_4577_142312">
					<rect
						width="1898"
						height="1466.34"
						fill="white"
						transform="translate(-88 -588)"
					/>
				</clipPath>
			</defs>
		</svg>

		<!-- Success -->
		<div v-if="message && message.success">
			<p
				v-if="message.message"
				role="status"
				class="font-light text-h2 mb-20"
				tabindex="-1"
				v-html="message.message"
			></p>
		</div>

		<!-- Content -->
		<div v-else class="relative pr-10">
			<p
				v-if="text"
				:id="`block-${$vnode.key}__description`"
				class="font-light text-h2 mb-20"
				v-html="text"
			></p>
			<LongReadTarget
				v-if="title"
				class="relative"
				:container-id="`block-${$vnode.key}`"
				:link-to-toc="{
					name: `Spring til &quot;${title}&quot; i indholdsfortegnelsen`,
					attrs: {
						class: 'bg-white text-red',
						'data-focus-ignore': true,
					},
				}"
				:title="title"
				:disabled="!showTitleInToc"
			>
				<BaseH2
					:id="`block-${$vnode.key}__title`"
					class="font-light text-h4"
					v-html="title"
				/>
			</LongReadTarget>

			<AxiosForm
				v-if="dataForm.endpointUrl"
				method="post"
				:action="dataForm.endpointUrl"
				:aria-labelledby="title ? `block-${$vnode.key}__title` : null"
				class="
					w-full
					mt-80
					>=1024:mt-64
					flex flex-col
					gap-y-18
					transition-opacity
					duration-200
				"
				:class="{
					'opacity-50': loading,
				}"
				@submit="onFormSubmit"
				@complete="onFormComplete"
				@error="onFormError"
			>
				<template v-for="(item, index) in dataForm.fields">
					<FormTextInput
						v-if="['text', 'email', 'number'].includes(item.type)"
						:key="index"
						v-bind="{ ...item, ...getSpecificBindings(item) }"
						v-model="item.value"
					/>

					<FormDropdown
						v-if="['dropdown'].includes(item.type)"
						:key="index"
						v-bind="{ ...item, ...getSpecificBindings(item) }"
						v-model="item.value"
					/>

					<div
						v-if="['button', 'submit'].includes(item.type)"
						:key="index"
						class="
							mt-12
							flex flex-col
							>=605:flex-row >=605:items-baseline
							gap-x-2xs/h gap-y-2xs/v
						"
					>
						<div
							v-if="message && !message.success"
							role="alert"
							class="text-input-form italic"
							tabindex="-1"
						>
							{{ message.message }}
						</div>

						<BaseButton
							tag="input"
							class="
								>=605:w-auto
								>=605:justify-self-end
								>=605:ml-auto
								bg-white
								hover:bg-theme-10
								text-theme
							"
							v-bind="{ ...item, ...getSpecificBindings(item) }"
						/>
					</div>
				</template>
			</AxiosForm>
		</div>
	</ScopedColorTheme>
</template>

<script>
import ScopedColorTheme from '~/components/main/ScopedColorTheme';
import { LongReadTarget } from '~/components/shared/LongRead';
import AxiosForm from '~/components/shared/AxiosForm';
import FormTextInput from '~/components/main/FormTextInput';
import FormDropdown from '~/components/main/FormDropdown';

export default {
	name: 'BlockListNewsletter',
	components: {
		ScopedColorTheme,
		LongReadTarget,
		AxiosForm,
		FormTextInput,
		FormDropdown,
	},
	inheritAttrs: false,

	props: {
		showTitleInToc: Boolean,
		title: String,
		text: String,
		form: {
			type: Object,
			default: () => ({}),
		},
	},

	data() {
		const dataForm = JSON.parse(JSON.stringify(this.form));
		dataForm?.fields?.forEach?.((field) => {
			if (!('value' in field)) {
				field.value = '';
			}
		});
		return {
			dataForm,
			message: null,
			loading: false,
		};
	},

	watch: {
		message() {
			this.$nextTick(() => {
				const focusEl = this.$el.querySelector(
					'[role="status"], [role="alert"]'
				);
				focusEl?.focus?.();
			});
		},
	},

	methods: {
		onFormSubmit() {
			this.message = null;
			this.loading = true;
		},

		onFormComplete({ data }) {
			this.message = data;
			this.loading = false;
		},

		onFormError() {
			this.message = {
				culture: 'da-DK',
				message: 'Noget gik galt. Prøv igen',
				statusCode: 500,
				success: false,
			};
			this.loading = false;
		},

		getSpecificBindings(field) {
			const bindings = {};

			// Text inputs
			if (['email'].includes(field.name)) {
				bindings.type = 'email';
			}
			if (['zipcode'].includes(field.name)) {
				bindings.type = 'number';
				bindings.min = 1000;
				bindings.max = 9990;
			}

			// Button inputs
			if (['button', 'submit'].includes(field.type)) {
				bindings.type = 'submit';
				bindings.label = null;
				bindings.value = field.label;
			}
			return bindings;
		},
	},
};
</script>

<style lang="postcss">
.c-block-list-newsletter {
	@apply relative bg-theme text-white px-md/h py-80 >=1024:pt-lg/v;
}
</style>
